// Excel export of Lao eTax documents by date range and submission status — the
// logic behind components/admin/documents/lao-etax-export-dialog.vue.
//
// Plain functions, no component, so it can be unit-tested: vue-jest here cannot
// parse this app's templates (see tests/unit/laoApprovedQuery.spec.js).
//
// ── Why the status filter runs in the browser ────────────────────────────
//
// "ยังไม่นำส่ง" is a document with NO '@LaoEtaxStatus' at all, and Firestore
// cannot match a missing field. Querying by status would also need a composite
// index per combination. So the query is doc type + date range — bounded on
// both sides and capped by EXPORT_LIMIT — and status is filtered on what comes
// back, which also lets the dialog count every status from one read.
//
// ── Why ED.IssueDateTime ─────────────────────────────────────────────────
//
// The same field and the same reasoning as libs/laoApprovedQuery.js: it is a
// fixed-width "YYYY-MM-DDTHH:mm:ss.SSS+07:00" string on every Lao document, so
// a bare "YYYY-MM-DD" is a valid lexicographic bound.

import firebase from "firebase/app";
import moment from "moment";
import { LAO_ETAX_STATUS_META } from "@/constants/laoEtaxStatus";
import { normalizeLaoRemark } from "@/libs/laoRemark";

const ISSUE_FIELD = "ED.IssueDateTime";
const DATE_FORMAT = "YYYY-MM-DD";

// One read past this tells us the range was truncated; the dialog then refuses
// rather than writing a file that silently misses documents. Sized for a year
// of one type at the busiest company (~17,000) with room to grow; past it the
// browser is the wrong place to build the file.
export const EXPORT_LIMIT = 30000;
// A year is the widest register anyone asks for; wider is a mistake.
export const MAX_RANGE_DAYS = 366;

// The status filter's key for a document that was never sent.
export const NOT_SENT = "none";

export const DOC_TYPE_CODE = { 1: "TX", 4: "CR", 6: "RT" };
const DOC_TYPE_LABEL = {
  TX: "ใบกำกับภาษี",
  CR: "ใบลดหนี้",
  RT: "ใบเสร็จรับเงิน/ใบกำกับภาษี",
};

// The amount columns are found by these strings, never by position — see
// buildSheet.
export const AMOUNT_HEADERS = [
  "ยอดก่อนภาษี (ใบลดหนี้ติดลบ)",
  "ภาษี (ใบลดหนี้ติดลบ)",
  "ยอดรวม (ใบลดหนี้ติดลบ)",
];

const NOT_SENT_COLOR = "#bdbdbd";

/** Every status the filter offers, never-sent first, with the chip colour. */
export function statusOptions() {
  return [{ value: NOT_SENT, text: "ยังไม่นำส่ง", color: NOT_SENT_COLOR }].concat(
    Object.keys(LAO_ETAX_STATUS_META).map((k) => ({
      value: k,
      text: LAO_ETAX_STATUS_META[k].label,
      color: LAO_ETAX_STATUS_META[k].color,
    }))
  );
}

/**
 * The quick ranges above the date fields. Each is a from/to pair the user
 * could have typed; "ปีนี้" runs to today, so it stays inside MAX_RANGE_DAYS.
 * @param {moment.Moment} [today]  injectable for tests
 */
export function datePresets(today) {
  const t = moment(today || undefined).startOf("day");
  const f = (m) => m.format(DATE_FORMAT);
  const lastMonth = t.clone().subtract(1, "month");
  return [
    { key: "thisMonth", label: "เดือนนี้", from: f(t.clone().startOf("month")), to: f(t) },
    {
      key: "lastMonth",
      label: "เดือนที่แล้ว",
      from: f(lastMonth.clone().startOf("month")),
      to: f(lastMonth.clone().endOf("month")),
    },
    {
      key: "last3Months",
      label: "3 เดือนล่าสุด",
      from: f(t.clone().subtract(2, "month").startOf("month")),
      to: f(t),
    },
    { key: "thisYear", label: "ปีนี้", from: f(t.clone().startOf("year")), to: f(t) },
  ];
}

/**
 * What the dialog shows before anyone downloads: how many documents per status
 * (for the chips) and the count and net total of what WOULD be exported.
 * @param {Array} rows       toExportRow results, already narrowed by doc type
 * @param {string[]} statuses  the status filter; empty = all
 */
export function summarize(rows, statuses) {
  const byStatus = {};
  rows.forEach((r) => {
    const k = r.status || NOT_SENT;
    byStatus[k] = (byStatus[k] || 0) + 1;
  });
  const picked = filterRows(rows, statuses, []);
  const net = picked.reduce((sum, r) => sum + (Number(r.cells[AMOUNT_HEADERS[2]]) || 0), 0);
  return { byStatus, count: picked.length, net: Math.round(net * 100) / 100 };
}

export function statusLabel(status) {
  if (!status) return "ยังไม่นำส่ง";
  const meta = LAO_ETAX_STATUS_META[status];
  return meta ? meta.label : status;
}

/**
 * Validate a from/to pair of "YYYY-MM-DD" (both inclusive) and return the
 * half-open string bounds of the query, or { error } in Thai.
 */
export function exportDateBounds(from, to) {
  const start = moment(from, DATE_FORMAT, true);
  const end = moment(to, DATE_FORMAT, true);
  if (!start.isValid() || !end.isValid()) return { error: "กรุณาเลือกวันที่ให้ครบ" };
  if (end.isBefore(start)) return { error: "วันที่สิ้นสุดต้องไม่ก่อนวันที่เริ่ม" };
  if (end.diff(start, "days") + 1 > MAX_RANGE_DAYS) {
    return { error: "เลือกช่วงได้ไม่เกิน " + MAX_RANGE_DAYS + " วัน" };
  }
  return {
    from: start.format(DATE_FORMAT),
    to: end.clone().add(1, "day").format(DATE_FORMAT),
  };
}

// Read in pages so a big range can report progress — one get() of 10,000
// documents is a long silence with no way to say how far along it is.
export const PAGE_SIZE = 500;

// Ranges longer than this are read only on request, not as soon as they are
// picked. Measured on the busiest Lao company (Oct 2026): ~2,000 documents a
// month at ~6 KB each, so a year of one type is ~17,000 documents and ~100 MB
// — not something to start downloading because someone clicked "ปีนี้".
export const AUTO_LOAD_MAX_DAYS = 93;

/** True when the range is long enough to wait for an explicit "โหลดข้อมูล". */
export function needsManualLoad(from, to) {
  const b = exportDateBounds(from, to);
  if (b.error) return false;
  return moment(b.to).diff(moment(b.from), "days") > AUTO_LOAD_MAX_DAYS;
}

/**
 * The Firestore query for one date range, ordered so it can be paged.
 *
 * With docType, the type is filtered by Firestore rather than after the read,
 * so the other types are never downloaded. That needs the composite index
 * `@DocType` + `ED.IssueDateTime` — which the documents list's own default
 * sort already uses, so it exists (checked on prod, both directions).
 */
export function buildExportQuery(db, companyID, bounds, docType) {
  const issue = new firebase.firestore.FieldPath(ISSUE_FIELD);
  let q = db.collection(`Companies/${companyID}/Docs`);
  if (docType) q = q.where(new firebase.firestore.FieldPath("@DocType"), "==", docType);
  return q.where(issue, ">=", bounds.from).where(issue, "<", bounds.to).orderBy(issue, "asc");
}

/**
 * Every document in the range as export rows, PAGE_SIZE at a time, reading at
 * most one past EXPORT_LIMIT so a truncated range is detected rather than
 * silently cut.
 *
 * Each page is turned into rows as it arrives and its snapshots dropped: a
 * row is a few hundred bytes, the document behind it ~6 KB with its line
 * items, so holding only rows keeps a year's read from piling up in memory.
 *
 * @param {object} o
 * @param {number} [o.docType]          filter by @DocType in the query
 * @param {function(id, data)} o.toRow  a row, or null to leave the document out
 * @param {function(number)} [o.onProgress]  documents read so far
 * @param {function(): boolean} [o.isStale]  true once the caller no longer wants
 *   this answer (the range changed); stops reading and resolves null
 * @returns {Promise<{ rows: Array, truncated: boolean } | null>}
 */
export async function fetchExportRows(db, companyID, bounds, o) {
  const base = buildExportQuery(db, companyID, bounds, o.docType);
  const rows = [];
  let read = 0;
  let last = null;
  while (read <= EXPORT_LIMIT) {
    let q = base.limit(PAGE_SIZE);
    if (last) q = q.startAfter(last);
    const snap = await q.get();
    if (o.isStale && o.isStale()) return null;
    snap.docs.forEach((d) => {
      read += 1;
      if (read > EXPORT_LIMIT) return;
      const row = o.toRow(d.id, d.data() || {});
      if (row) rows.push(row);
    });
    if (o.onProgress) o.onProgress(Math.min(read, EXPORT_LIMIT));
    if (snap.docs.length < PAGE_SIZE) break;
    last = snap.docs[snap.docs.length - 1];
  }
  return { rows, truncated: read > EXPORT_LIMIT };
}

// A literal dotted key first (Docs store "SCTT.AHTA.BTP.Name" as one key),
// then a real nested path for maps such as @DocData.
function readField(data, key) {
  if (Object.prototype.hasOwnProperty.call(data, key)) return data[key];
  return key.split(".").reduce((o, k) => (o && typeof o === "object" ? o[k] : undefined), data);
}

// Firestore Timestamp | Date | string -> "YYYY-MM-DD HH:mm", or "".
function formatTime(v) {
  if (!v) return "";
  const d = typeof v.toDate === "function" ? v.toDate() : v;
  const m = moment(d);
  return m.isValid() ? m.format("YYYY-MM-DD HH:mm") : String(v);
}

// Stored in satang; a credit note is negative so a plain SUM is the net — the
// convention of approved-export-dialog.vue and the Thai tax report.
function signed(data, key, isCredit) {
  const n = (Number(data[key]) || 0) / 100;
  return isCredit ? -n : n;
}

/**
 * One document -> one export row (an object; buildSheet orders the columns).
 * @param {string} id
 * @param {object} data  the Firestore document
 * @param {Array<{id,key,label}>} extraColumns  constants/laoEtaxColumns entries
 */
export function toExportRow(id, data, extraColumns) {
  const code = DOC_TYPE_CODE[data["@DocType"]] || String(data["@DocType"] || "");
  const isCredit = code === "CR";
  const issue = data[ISSUE_FIELD] || "";
  const row = {
    id: data.objectID || id,
    docType: Number(data["@DocType"]),
    status: data["@LaoEtaxStatus"] || "",
    cells: {
      "วันที่เอกสาร": issue ? String(issue).split("T")[0] : "",
      "เลขที่เอกสาร": data["ED.ID"] || data.objectID || id,
      "ประเภท": DOC_TYPE_LABEL[code] || code,
      "ชื่อผู้ซื้อ": data["SCTT.AHTA.BTP.Name"] || "",
      "เลขผู้เสียภาษี": data["SCTT.AHTA.BTP.STR.ID"] || "",
      [AMOUNT_HEADERS[0]]: signed(data, "SCTT.AHTS.STSHMS.TaxBasisTotalAmount", isCredit),
      [AMOUNT_HEADERS[1]]: signed(data, "SCTT.AHTS.STSHMS.TaxTotalAmount", isCredit),
      [AMOUNT_HEADERS[2]]: signed(data, "SCTT.AHTS.STSHMS.GrandTotalAmount", isCredit),
      "สถานะ Lao eTax": statusLabel(data["@LaoEtaxStatus"]),
      "เลขที่ Lao eTax": data["@LaoEtaxInvoiceNumber"] || "",
      "เวลาออกใบ (gateway)": data["@LaoEtaxIssueTime"] || "",
      "รหัสข้อผิดพลาด": data["@LaoEtaxErrorCode"] || "",
      "ข้อผิดพลาด": data["@LaoEtaxErrorMessage"] || "",
      "นำส่งครั้งแรกโดย": data["@LaoEtaxIssuedBy"] || "",
      "นำส่งล่าสุด": formatTime(data["@LaoEtaxLastSentAt"]),
      "จำนวนครั้งที่ส่ง": Number(data["@LaoEtaxSendCount"]) || 0,
      "ลิงก์ตรวจสอบ": data["@LaoEtaxUrl"] || "",
      // The customer's own note, in full — the table's ellipsis is for one line
      // of a screen, not for a file someone reads the note out of.
      "หมายเหตุ": normalizeLaoRemark((data["@DocData"] || {}).remark),
    },
  };
  (extraColumns || []).forEach((c) => {
    const v = readField(data, c.key);
    row.cells[c.label] = v === undefined || v === null ? "" : String(v);
  });
  return row;
}

/**
 * Header order: identity, the company's extra columns, amounts, Lao eTax state,
 * then the free-text note last so a long one doesn't push the amounts away.
 */
export function exportHeader(extraColumns) {
  return ["วันที่เอกสาร", "เลขที่เอกสาร", "ประเภท", "ชื่อผู้ซื้อ", "เลขผู้เสียภาษี"]
    .concat((extraColumns || []).map((c) => c.label))
    .concat(AMOUNT_HEADERS)
    .concat([
      "สถานะ Lao eTax",
      "เลขที่ Lao eTax",
      "เวลาออกใบ (gateway)",
      "รหัสข้อผิดพลาด",
      "ข้อผิดพลาด",
      "นำส่งครั้งแรกโดย",
      "นำส่งล่าสุด",
      "จำนวนครั้งที่ส่ง",
      "ลิงก์ตรวจสอบ",
      "หมายเหตุ",
    ]);
}

/**
 * Keep the rows a filter asks for.
 * @param {Array} rows       toExportRow results
 * @param {string[]} statuses  status keys, NOT_SENT for never-sent; empty = all
 * @param {number[]} docTypes  @DocType values; empty = all
 */
export function filterRows(rows, statuses, docTypes) {
  const s = statuses || [];
  const t = docTypes || [];
  return rows.filter((r) => {
    if (t.length && t.indexOf(r.docType) === -1) return false;
    if (!s.length) return true;
    return s.indexOf(r.status || NOT_SENT) !== -1;
  });
}

/** Rows -> array of arrays, header first, then a net-total row. */
export function buildSheet(rows, extraColumns) {
  const header = exportHeader(extraColumns);
  const amountCols = AMOUNT_HEADERS.map((h) => header.indexOf(h));
  const totals = amountCols.map(() => 0);
  const body = rows.map((r) => {
    const line = header.map((h) => (r.cells[h] === undefined ? "" : r.cells[h]));
    amountCols.forEach((c, i) => {
      totals[i] += Number(line[c]) || 0;
    });
    return line;
  });
  const totalRow = header.map(() => "");
  totalRow[amountCols[0] - 1] = "รวมสุทธิ";
  amountCols.forEach((c, i) => {
    totalRow[c] = Math.round(totals[i] * 100) / 100;
  });
  return { header, amountCols, aoa: [header].concat(body).concat([totalRow]) };
}
